import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { setUrlId } from '@/lib/url-utils';
import { restoredPages } from './usePersistedPath';

// Module-level: survives SPA navigation, cleared on full page reload.
// This is the key distinction from sessionStorage — editing the URL bar
// triggers a full reload which resets this, so stale IDs aren't restored.
const savedIds: Record<string, string | undefined> = {};

/** Clears all saved deep-link IDs. Called on logout. */
export const clearSavedIds = () => {
  for (const key of Object.keys(savedIds)) delete savedIds[key];
};

/**
 * Preserves `?id=` across SPA page switches (e.g., schedules → playlists → back).
 *
 * On every `id` change, saves to a module-level variable.
 * On mount (SPA navigation back), restores `?id=` if the URL doesn't already have one.
 * Full page reloads clear module-level state, so manually clearing `?id=` works correctly.
 *
 * Saves happen in an effect body (not cleanup), so unmount during logout
 * can't re-populate state after fullLogout's clearSavedIds().
 */
export function useDeepLinkRestore(pageKey: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('id') ?? undefined;
  const idRef = useRef(id);
  idRef.current = id;

  // On mount: restore saved id if URL doesn't have one
  useEffect(() => {
    // If usePersistedPath already restored params (including id) for this page,
    // skip to avoid a competing setSearchParams call that would overwrite the path.
    if (restoredPages.has(pageKey)) {
      restoredPages.delete(pageKey);
      delete savedIds[pageKey];
    } else if (!idRef.current && savedIds[pageKey]) {
      const idToRestore = savedIds[pageKey] ?? '';
      delete savedIds[pageKey];
      setUrlId(setSearchParams, idToRestore);
    } else {
      delete savedIds[pageKey];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // Save id on every change. Effect bodies don't fire on unmount (only cleanup
  // functions do), so fullLogout's clearSavedIds() can't be undone by unmount.
  useEffect(() => {
    savedIds[pageKey] = id;
  }, [id, pageKey]);
}
